import axios from 'axios'
import { useQueries } from '@tanstack/react-query'
import { GenNode } from '../../../../types/carbon/models/GenNode'
import { MultiTableConfigTable } from '../../../../types/gotypes'
import { API_BASE_URL } from '../../../../constants/system'
import { tocTablePaths } from './tocTables'
import { configuredTableRefs, findMissingTableRefs, ConfiguredSourceRef } from './wizardHelpers'

export interface UseMissingTableReferencesArgs {
  customer: string
  /** The dashboard's job. A table that records the job it was picked from
   * (`MultiTableConfigTable.job`) is checked in that job instead. */
  job: string
  /** The multi-table's currently configured tables (already loaded by the
   * caller, e.g. via `useDashboardMultiTable`) — avoids a second fetch of the
   * same record. */
  tables: MultiTableConfigTable[]
  /** Only runs the TOC check when the card is otherwise ready to display a
   * report — no point fetching TOC for a card with no analysis yet, and
   * `tables` may not have loaded (or be reachable, e.g. an unauthenticated
   * share-link viewer) in which case this is silently a no-op. */
  enabled: boolean
}

export interface MissingTableReferencesResult {
  missing: ConfiguredSourceRef[]
  loading: boolean
}

/**
 * Cross-checks a Gen AI Insights card's configured table references against
 * the live TOC, to power the "no longer available" display (#970 Part C).
 * Each table is checked in the job it was picked from (`table.job`, else the
 * dashboard's job): a card may read tables from another job, and checking
 * those in the dashboard's job hid a working report (Sky, 13 Oct).
 * Frontend-only — reuses the existing TOC endpoint, no API changes.
 */
export function useMissingTableReferences({
  customer,
  job,
  tables,
  enabled,
}: UseMissingTableReferencesArgs): MissingTableReferencesResult {
  const active = Boolean(enabled && customer && job && tables.length > 0)
  const configured = configuredTableRefs(tables)
  const jobOf = (ref: ConfiguredSourceRef) => ref.job || job
  const jobs = active ? [...new Set(configured.map(jobOf))] : []

  const tocQueries = useQueries({
    queries: jobs.map(j => ({
      queryKey: ['toc', customer, j],
      queryFn: async () => {
        const res = await axios.get<{ toc: GenNode[] }>(`${API_BASE_URL}/job/${customer}/${j}/toc`)
        return res.data.toc || []
      },
    })),
  })

  if (!active || tocQueries.some(q => !q.data)) {
    return { missing: [], loading: active && tocQueries.some(q => q.isLoading) }
  }

  const validPathsByJob = new Map(jobs.map((j, i) => [j, tocTablePaths(tocQueries[i].data!)]))
  const missing = configured.filter(ref => findMissingTableRefs([ref], validPathsByJob.get(jobOf(ref))!).length > 0)

  return { missing, loading: false }
}

export default useMissingTableReferences
